import { ValidationErrors } from "final-form";
import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { RootState, useSelector } from "src/store";

import { GroupTree } from "@dashboard/files/components/GroupTree/GroupTree";
import { UploadProgressBar } from "@dashboard/files/components/UploadProgressBar";
import UploadTable from "@dashboard/files/components/UploadTable/UploadTable";
import { selectGroupChildrenIds, selectGroups } from "@dashboard/groups/store/selectors";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { FileExplorerHeader } from "@dashboard/shared/components/SidePanel/shared/FileExplorerHeader";
import { useCurrentWorkspace, useIsWorkspaceFeatureLimitReached } from "@dashboard/workspaces/api/helpers";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { UploadFileField } from "@shared/form/UploadFileField";
import { Tooltip } from "@shared/Tooltip";

import { UploadProvider, useUploadContext } from "./context/UploadContext";
import { useFilePageTree } from "./hooks/useFilePageTree";
import { useStyles } from "./UploadPage.style";

// 256 MB in bytes
export const maxFileSize = 256 * 1024 * 1024;

interface FormValues {
    files: File[];
    target: number | undefined;
}

type UploadTarget = { groupId: number | undefined; groupName: string | undefined; isWorkspace: boolean };

const getUploadTarget = (params: {
    selectedGroupId: number | null;
    groupsToUpload: API.Group[];
    groups: API.Group[];
}): UploadTarget => {
    const { selectedGroupId, groups } = params;

    if (selectedGroupId !== null) {
        const group = groups.find((g) => g.id === selectedGroupId);
        return { groupId: selectedGroupId, groupName: group?.name, isWorkspace: false };
    }

    return { groupId: undefined, groupName: undefined, isWorkspace: true };
};

const UploadPageContent = () => {
    const { classes } = useStyles();
    const { data: workspace } = useCurrentWorkspace();
    const { t } = useTranslation(["common", "pages"]);

    const [userSelectedGroupId, setUserSelectedGroupId] = useState<number | null | undefined>(undefined);
    const [includeParentFiles, setIncludeParentFiles] = useState(true);
    const { isOpen: treeOpen, onToggle: handleTreeToggle } = useFilePageTree();

    const handleGroupSelect = (groupId: number | null) => {
        setUserSelectedGroupId(groupId);
    };

    const { workspaceAbility } = PermissionService();
    const { uploadStarted } = useUploadContext();
    const cannotUploadFile = workspaceAbility(workspace).cannot("manage", "File");
    const groups = useSelector(selectGroups);

    const currentUser = AuthService.getCurrentUser()?.email;

    const userGroups = groups.filter((group) => group.members.some((member) => member.email === currentUser));
    const nestedGroupIds = useSelector(
        (state: RootState) => Array.from(new Set(userGroups.flatMap((group) => selectGroupChildrenIds(state, group.id)))),
        shallowEqual,
    );
    const allNestedGroups = groups.filter((group) => nestedGroupIds.includes(group.id));
    const groupsToUpload = [...userGroups, ...allNestedGroups];

    const isOverlimit = useIsWorkspaceFeatureLimitReached("FileCountLimit");

    const defaultSelectedGroupId = cannotUploadFile && userGroups[0] ? userGroups[0].id : null;
    const selectedGroupId = userSelectedGroupId === undefined ? defaultSelectedGroupId : userSelectedGroupId;

    const noGroupSelected = cannotUploadFile && selectedGroupId === null;
    const noTarget = (!workspace && groupsToUpload.length === 0) || noGroupSelected;
    const disabledUpload = uploadStarted || isOverlimit || noTarget;

    const validate = (values: FormValues): ValidationErrors => {
        const errors: ValidationErrors = {};
        if (values.files) {
            for (const file of values.files) {
                if (file.size > maxFileSize) {
                    errors.files = t("pages:filesPage.input.uploadField.validation.fileSizeExceeded");
                    break;
                }
            }
        }
        return errors;
    };

    const getTooltipTitle = () => {
        if (isOverlimit) {
            return t("pages:filesPage.tooltips.overlimit");
        }
        if (uploadStarted) {
            return t("pages:filesPage.tooltips.uploadInProgress");
        }
        if (noTarget) {
            return t("pages:filesPage.tooltips.noTarget");
        }
        return "";
    };

    const uploadTarget = getUploadTarget({
        selectedGroupId,
        groupsToUpload,
        groups,
    });

    const uploadTargetName = uploadTarget.groupName
        ? uploadTarget.groupName
        : uploadTarget.isWorkspace
          ? t("pages:filesPage.input.uploadField.workspaceLabel")
          : undefined;

    return (
        <PageContainer>
            <PageHeader
                title={t("pages:filesPage.title")}
                iconName="fa-regular fa-file"
                description={t("pages:filesPage.description")}
            />
            <div className={classes.pageLayout}>
                <GroupTree
                    selectedGroupId={selectedGroupId}
                    onSelect={handleGroupSelect}
                    isOpen={treeOpen}
                    onToggle={handleTreeToggle}
                />
                <div className={classes.mainContent}>
                    <FileExplorerHeader
                        selectedGroupId={selectedGroupId}
                        includeParentFiles={includeParentFiles}
                        onIncludeParentFilesChange={setIncludeParentFiles}
                        onSelect={handleGroupSelect}
                    />
                    <ExpiredWrapper>
                        <Form<FormValues>
                            onSubmit={() => undefined}
                            validate={validate}
                            render={({ handleSubmit, values }) => (
                                <form onSubmit={handleSubmit}>
                                    <div className={classes.dropzoneContainer}>
                                        <Tooltip
                                            placement="top"
                                            className={classes.uploadTooltipContainer}
                                            title={getTooltipTitle()}
                                            hide={!disabledUpload}
                                        >
                                            <UploadFileField
                                                name="files"
                                                disabled={disabledUpload}
                                                groupId={uploadTarget.groupId}
                                                uploadTargetName={uploadTargetName}
                                            />
                                        </Tooltip>

                                        {uploadStarted ? (
                                            <div className={classes.uploadContainer}>
                                                <div className={classes.contentBox}>
                                                    <UploadProgressBar filesToUpload={values?.files || []} />
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </form>
                            )}
                        />
                    </ExpiredWrapper>
                    <UploadTable selectedGroupId={selectedGroupId} includeParentFiles={includeParentFiles} />
                </div>
            </div>
        </PageContainer>
    );
};

const UploadPage = () => {
    return (
        <UploadProvider>
            <UploadPageContent />
        </UploadProvider>
    );
};

export default withMetaDescription(UploadPage, {
    title: i18next.t("pages:filesPage.metaTitle"),
    description: i18next.t("pages:filesPage.metaDescription"),
});
